"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    product: [
      { label: 'Create Plan', href: '/input-form' },
      { label: 'AI Optimizer', href: '/ai-plan' },
    ],
    resources: [
      { label: 'Blog', href: '/blog' },
      { label: 'Space Tips', href: '/blog' },
      { label: 'Design Ideas', href: '/blog' },
    ],
    company: [
      { label: 'About Us', href: '/home#about' },
      { label: 'Contact', href: '/home#contact' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms-of-service' },
    ],
    support: [
      { label: 'Help Center', href: '/help-center' },
      { label: 'Account', href: '/account' },
      { label: 'Login', href: '/login' },
    ]
  };

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const validateEmail = (e: string) => {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);
  };

  const handleSubscribe = async () => {
    setMessage(null);
    if (!validateEmail(email)) {
      setMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage(data?.error || 'Subscription failed. Please try again later.');
      } else {
        setMessage('Subscribed! Check your inbox for a confirmation.');
        setEmail('');
      }
    } catch (err) {
      console.error('Subscribe error', err);
      setMessage('Subscription failed. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link href="/home" className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <svg
                  viewBox="0 0 24 24"
                  className="w-5 h-5 text-primary-foreground"
                  fill="currentColor"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">
                MicroSpace Optimizer
              </span>
            </Link>
            <p className="text-muted-foreground mb-6 max-w-md">
              Transform your small spaces into functional, beautiful environments with AI-powered
              optimization solutions. Get personalized recommendations tailored to your unique needs.
              
              <span className="block mt-4 pt-4 border-t border-border/50">
                <span className="flex items-center gap-2 font-semibold text-foreground text-sm mb-1">
                  <Icon name="HeartIcon" size={14} className="text-red-500" />
                  Support the project
                </span>
                <span className="text-sm block mb-2">Help us to keep it free.</span>
                <a
                  href="https://paypal.me/tgrowgreen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 bg-[#0070BA] text-white rounded-md hover:bg-[#003087] transition-colors text-xs font-medium"
                >
                  Donate via PayPal
                </a>
              </span>
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">
              Product
            </h3>
            <ul className="space-y-2">
              {footerLinks?.product?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">
              Resources
            </h3>
            <ul className="space-y-2">
              {footerLinks?.resources?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">
              Support
            </h3>
            <ul className="space-y-2">
              {footerLinks?.support?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Stay Updated
              </h3>
              <p className="text-muted-foreground text-sm">
                Get the latest space optimization tips and AI insights delivered to your inbox.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:max-w-md">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder-muted-foreground text-sm"
              />
              <button
                onClick={handleSubscribe}
                disabled={isSubmitting}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium whitespace-nowrap disabled:opacity-50"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>
            {message && (
              <div className="mt-3 text-sm text-center text-foreground">{message}</div>
            )}
          </div>
        </div>

        {/* Donation Section */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Support the Project
              </h3>
              <p className="text-muted-foreground text-sm max-w-md">
                Help us keep MicroSpace Optimizer free and accessible for everyone. Your support empowers us to grow and improve.
              </p>
            </div>
            <a
              href="https://paypal.me/tgrowgreen"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2.5 bg-[#0070BA] hover:bg-[#003087] text-white rounded-lg transition-colors text-sm font-medium flex items-center shadow-sm"
            >
              <span className="mr-2">Donate with PayPal</span>
              <Icon name="HeartIcon" size={16} className="text-white" />
            </a>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 pt-8 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 text-sm text-muted-foreground">
            <p>
              © {currentYear} MicroSpace Optimizer. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Link
                href="/privacy-policy"
                className="hover:text-foreground transition-colors"
              >
                Privacy Policy
              </Link>
              <span>•</span>
              <Link
                href="/terms-of-service"
                className="hover:text-foreground transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center text-sm text-muted-foreground">
            <Icon name="HeartIcon" size={16} className="mr-1 text-red-500" />
            <span>Made with passion for better living spaces</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;